import { ButtonHTMLAttributes, forwardRef } from 'react';

import { regPlatformTheme } from '@/styles/regPlatformTheme';

import { FiMoreVertical } from 'react-icons/fi';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { ActionsVariant } from './types';

export type ActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ActionsVariant;
  opener?: boolean;
};

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>((props, ref) => {
  const { children, variant = 'primary', opener = false, ...rest } = props;

  return (
    <StyledButton ref={ref} variant={variant} opener={opener} {...rest}>
      {opener ? <FiMoreVertical /> : children}
    </StyledButton>
  );
});

ActionButton.displayName = 'ActionButton';

const StyledButton = styled.button<Pick<ActionButtonProps, 'variant' | 'opener'>>`
  && {
    font-family: Arial;
    font-weight: 700;
    font-size: 16px;
    line-height: 16px;
    min-height: 32px;
    text-align: center;
    padding: ${({ opener }) => (opener ? '0' : '8px 24px 8px 24px')} !important;
    border-radius: ${({ opener }) => (opener ? '50%' : '8px')};

    &:hover:not([disabled]) {
      cursor: pointer;
    }

    &[disabled] {
      background: ${regPlatformTheme.colors.lightGrey};
      color: ${regPlatformTheme.colors.darkGrey};
    }

    ${({ variant }) => {
      switch (variant) {
        case 'primary':
          return css`
            background: white;
            color: ${regPlatformTheme.colors.darkPurple};
            border: 1px solid ${regPlatformTheme.colors.darkPurple};
            &:hover:not([disabled]) {
              background: #d3b8ff4d;
            }
            &:active:not([disabled]) {
              color: white;
              background: ${regPlatformTheme.colors.darkPurple};
            }
            &[disabled] {
              border: none;
            }
          }
        `;
        case 'secondary':
          return css`
            background: ${regPlatformTheme.colors.darkPurple};
            color: white;
            &:hover:not([disabled]) {
              background-image: linear-gradient(rgb(0 0 0/20%) 0 0);
            }
            &:active:not([disabled]) {
              background-image: linear-gradient(rgb(0 0 0/40%) 0 0);
            }
          `;
        case 'tertiary':
          return css`
            background: #efeafb;
            color: ${regPlatformTheme.colors.darkPurple};
            border: 1px solid ${regPlatformTheme.colors.darkPurple};
            &:hover:not([disabled]) {
              background-image: linear-gradient(rgb(0 0 0/5%) 0 0);
            }
            &:active:not([disabled]) {
              background-image: linear-gradient(rgb(0 0 0/10%) 0 0);
            }
            &[disabled] {
              border: none;
            }
          }
        `;
      }
    }}

    ${({ opener }) => {
      return (
        opener &&
        css`
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
        `
      );
    }}
  }
`;
